import React, { useEffect, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Layers,
  X
} from "lucide-react";

import {
  getAllExamTypes,
  createExamType,
  updateExamType,
  deleteExamType
} from "../../api/examsschedule/examTypeApi";
import {
  getAllExamTypesPortal,
  createExamTypePortal,
  updateExamTypePortal,
  deleteExamTypePortal
} from "../../api/examsschedule/examTypePortalApi";
import {
  useStaffAuth
} from "../../context/StaffAuthContext";
import {
  pickList,
  toId
} from "../../utils/examsschedule/examHelpers";
import {
  Button,
  Input,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  EmptyState,
  Skeleton,
  Checkbox
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const SCHEDULE_PROFILE_OPTIONS = [
  { value: "LIGHT", label: "Light Profile" },
  { value: "STANDARD", label: "Standard Profile" },
  { value: "HEAVY", label: "Heavy Profile" },
];

const ExamTypeList = ({ isPortal = false }) => {
  const staffAuth = useStaffAuth();
  const canManage = isPortal
    ? staffAuth?.hasPermission?.("EXAM_MANAGEMENT_FULL")
    : true;

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    code: "",
    name: "",
    scheduleProfile: "LIGHT",
    description: "",
  });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const toast = useToast();

  const load = async () => {
    try {
      setLoading(true);
      const res = isPortal
        ? await getAllExamTypesPortal({})
        : await getAllExamTypes({});
      setTypes(pickList(res, ["types", "data"]));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortal]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code || !form.name || !form.scheduleProfile) {
      toast.error("Code, name and profile are required");
      return;
    }
    try {
      setSubmitting(true);
      if (isPortal) {
        await createExamTypePortal(form);
      } else {
        await createExamType(form);
      }
      toast.success("Exam type created");
      setForm({
        code: "",
        name: "",
        scheduleProfile: "LIGHT",
        description: "",
      });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (t) => {
    const id = toId(t);
    setEditId(id);
    setEditForm({
      code: t.code || "",
      name: t.name || "",
      scheduleProfile: t.scheduleProfile || "LIGHT",
      description: t.description || "",
      isActive: t.isActive !== false,
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editId) return;
    try {
      setSubmitting(true);
      if (isPortal) {
        await updateExamTypePortal(editId, editForm);
      } else {
        await updateExamType(editId, editForm);
      }
      toast.success("Exam type updated");
      setEditId(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete or deactivate this type?")) return;
    try {
      if (isPortal) {
        await deleteExamTypePortal(id);
      } else {
        await deleteExamType(id);
      }
      toast.success("Exam type removed");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
          Exam Types
        </h1>
        <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
          Manage evaluation categories and grading profiles
        </p>
      </div>

      {/* Notifications */}
      
      

      {/* Responsive Layout Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Creation Form Sidebar */}
        {canManage && (
          <Card className="lg:col-span-4 w-full">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
                <Plus className="h-4 w-4 text-primary-900 dark:text-primary-300 shrink-0" />
                Add New Exam Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreate} className="space-y-4">
                <Input
                  label="Code *"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="e.g. MID_TERM"
                  required
                />
                <Input
                  label="Name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Mid Term Exam"
                  required
                />
                <Select
                  label="Schedule Profile *"
                  options={SCHEDULE_PROFILE_OPTIONS}
                  value={form.scheduleProfile}
                  onChange={(val) => setForm({ ...form, scheduleProfile: val })}
                />
                <Input
                  label="Description"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="Brief description"
                />
                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  isLoading={submitting}
                >
                  Create Type
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Directory Card */}
        <Card className={canManage ? "lg:col-span-8 w-full" : "lg:col-span-12 w-full"}>
          <CardHeader>
            <CardTitle className="text-base font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
              <Layers className="h-4 w-4 text-primary-900 dark:text-primary-300 shrink-0" />
              Existing Exam Types ({types.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 space-y-3">
                <Skeleton height={32} width="100%" />
                <Skeleton height={32} width="100%" />
                <Skeleton height={32} width="100%" />
              </div>
            ) : types.length === 0 ? (
              <div className="p-6">
                <EmptyState
                  icon={Layers}
                  title="No Exam Types Configured"
                  description="Create an exam type to get started with creating exam schedules."
                />
              </div>
            ) : (
              <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {types.map((t) => {
                  const id = toId(t);
                  const isEditing = editId === id;

                  if (isEditing) {
                    return (
                      <div key={id} className="p-4 bg-primary-50/50 space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                            Edit Exam Type
                          </h4>
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={() => setEditId(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <form
                          onSubmit={handleUpdate}
                          className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                        >
                          <Input
                            label="Code"
                            value={editForm.code}
                            onChange={(e) =>
                              setEditForm({ ...editForm, code: e.target.value })
                            }
                            required
                          />
                          <Input
                            label="Name"
                            value={editForm.name}
                            onChange={(e) =>
                              setEditForm({ ...editForm, name: e.target.value })
                            }
                            required
                          />
                          <Select
                            label="Schedule Profile"
                            options={SCHEDULE_PROFILE_OPTIONS}
                            value={editForm.scheduleProfile}
                            onChange={(val) =>
                              setEditForm({ ...editForm, scheduleProfile: val })
                            }
                          />
                          <Input
                            label="Description"
                            value={editForm.description}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                description: e.target.value,
                              })
                            }
                          />
                          <div className="sm:col-span-2 pt-1">
                            <Checkbox
                              id={`active-${id}`}
                              label="Active"
                              checked={Boolean(editForm.isActive)}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  isActive: e.target.checked,
                                })
                              }
                            />
                          </div>
                          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => setEditId(null)}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              variant="primary"
                              size="sm"
                              isLoading={submitting}
                            >
                              Save Changes
                            </Button>
                          </div>
                        </form>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={id}
                      className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                    >
                      <div className="space-y-1.5 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-neutral-900 dark:text-neutral-100 text-sm">
                            {t.name}
                          </span>
                          <Badge variant="default">{t.code}</Badge>
                          <Badge
                            variant={
                              t.scheduleProfile === "HEAVY"
                                ? "danger"
                                : t.scheduleProfile === "STANDARD"
                                ? "primary"
                                : "warning"
                            }
                          >
                            {t.scheduleProfile}
                          </Badge>
                          {t.isActive === false && (
                            <Badge variant="danger">Inactive</Badge>
                          )}
                        </div>
                        {t.description && (
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 line-clamp-2">
                            {t.description}
                          </p>
                        )}
                      </div>

                      {canManage && (
                        <div className="flex items-center gap-2 self-start sm:self-center shrink-0 pt-2 sm:pt-0">
                          <Button
                            variant="secondary"
                            size="sm"
                            leftIcon={<Pencil className="h-3.5 w-3.5" />}
                            onClick={() => startEdit(t)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                            onClick={() => handleDelete(id)}
                          >
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ExamTypeList;